import { CheckboxControl, ComboBoxControl } from "@aurem/components";
import {
  Banner,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  type SelectOption,
} from "@aurem/ui";
import { useLanguageSwitcher } from "hooks/useLanguageSwitcher";
import { AlertTriangle, Globe } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { FormProvider, UseFormReturn } from "react-hook-form";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  citizenshipOptions: SelectOption[];
  onConfirm: () => void;
  form: UseFormReturn<{
    citizenships: SelectOption[];
    citizenshipConfirmation: boolean;
  }>;
};

export const CitizenshipConfirmationModal = ({
  open,
  onOpenChange,
  citizenshipOptions,
  onConfirm,
  form,
}: Props) => {
  const { t } = useI18n();
  const { currentLanguage, handleLanguageChange } = useLanguageSwitcher();

  const connections = [
    t(
      "apps.employee.pages.citizenshipConfirmation.modal.connections.citizenship",
    ),
    t("apps.employee.pages.citizenshipConfirmation.modal.connections.birth"),
    t(
      "apps.employee.pages.citizenshipConfirmation.modal.connections.residence",
    ),
    t("apps.employee.pages.citizenshipConfirmation.modal.connections.tax"),
  ];

  const toggleLanguage = () => {
    const newLang = currentLanguage === "en" ? "ar" : "en";
    void handleLanguageChange(newLang);
  };

  return (
    <FormProvider {...form}>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={t("apps.employee.pages.citizenshipConfirmation.modal.title")}
        preventClose={true}
        showClose={false}
      >
        <ModalBody className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <Text size="sm" weight="medium">
              {t("apps.employee.pages.citizenshipConfirmation.modal.heading")}
            </Text>
            <div className="text-neutral-500/70">
              <Text size="sm">
                {t(
                  "apps.employee.pages.citizenshipConfirmation.modal.description",
                )}
              </Text>
              <ul className="mt-1.5 list-disc space-y-1.5 ps-5 text-sm">
                {connections.map((connection) => (
                  <li key={connection}>{connection}</li>
                ))}
              </ul>
            </div>
          </div>
          <ComboBoxControl
            name="citizenships"
            options={citizenshipOptions}
            placeholder={t(
              "apps.employee.pages.citizenshipConfirmation.modal.placeholder",
            )}
            multiple
          />
          <CheckboxControl
            name="citizenshipConfirmation"
            required
            variant="card"
            className="bg-neutral-500/5"
            checkboxPosition="start"
            size="sm"
            label={t(
              "apps.employee.pages.citizenshipConfirmation.modal.checkbox",
            )}
          />
          <Banner
            color="warning"
            icon={<AlertTriangle />}
            variant="card"
            layout="stacked"
            className="text-xs items-start"
            title={
              <span className="uppercase">
                {t(
                  "apps.employee.pages.citizenshipConfirmation.modal.disclaimer.title",
                )}
              </span>
            }
            text={t(
              "apps.employee.pages.citizenshipConfirmation.modal.disclaimer.text",
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            onClick={toggleLanguage}
            variant="ghost"
            color="neutral"
            size="sm"
            startIcon={<Globe className="w-5 h-5" />}
          >
            {currentLanguage.toUpperCase()}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={
              !form.watch("citizenships")?.length ||
              !form.watch("citizenshipConfirmation")
            }
          >
            {t("apps.employee.pages.citizenshipConfirmation.button")}
          </Button>
        </ModalFooter>
      </Modal>
    </FormProvider>
  );
};
